import React from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { CheckCircleOutline as GoodIcon } from '@mui/icons-material';
import { PHOTO_ISSUE_LABELS } from '../utils/inspections';

function issueText(issues = []) {
  return issues.map((i) => PHOTO_ISSUE_LABELS[i] || i).join(' · ');
}

/**
 * Result of the automatic photo check under one inspection photo (ZRF-INSP-AI-01).
 * The photo is always saved; this only suggests a retake.
 */
function InspectionPhotoCheck({ check, readOnly = false, onRetake, onKeep, onRetry }) {
  if (!check) return null;

  if (check.state === 'pending') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }} role="status">
        <CircularProgress size={12} />
        <Typography variant="caption" color="text.secondary">
          Checking photo…
        </Typography>
      </Box>
    );
  }

  if (check.state === 'failed') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {check.errorCode === 'rate_limited'
            ? 'Photo checks are paused for today. Your photo is saved.'
            : "Couldn't check this photo. It's saved as is."}
        </Typography>
        {!readOnly && onRetry && check.errorCode !== 'rate_limited' && (
          <Button size="small" onClick={onRetry} sx={{ minWidth: 0, py: 0 }}>
            Try again
          </Button>
        )}
      </Box>
    );
  }

  if (check.usable) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'success.main' }}>
        <GoodIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption">Looks good</Typography>
      </Box>
    );
  }

  if (check.keptDespiteIssues || readOnly) {
    return (
      <Typography variant="caption" color="text.secondary">
        {check.keptDespiteIssues ? 'Kept' : 'Retake suggested'}: {issueText(check.issues)}
      </Typography>
    );
  }

  return (
    <Alert
      severity="warning"
      sx={{ py: 0, px: 1, alignItems: 'center', '& .MuiAlert-message': { py: 0.5 } }}
      action={
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" color="inherit" component="label">
            Retake
            <input
              hidden
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) onRetake(file);
              }}
            />
          </Button>
          <Button size="small" color="inherit" onClick={onKeep}>
            Keep anyway
          </Button>
        </Box>
      }
    >
      <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
        Retake suggested: {issueText(check.issues)}
      </Typography>
      {check.tip && (
        <Typography variant="caption" component="div">
          {check.tip}
        </Typography>
      )}
    </Alert>
  );
}

export default InspectionPhotoCheck;
